import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useQuery } from '@tanstack/react-query';
import Loader from 'components/Kizen/Loader';
import { PageSizing } from 'components/Layout/PageContentWidth';
import { getBusinessLicenseQuery, getSummaryUsageQuery } from 'query/billing';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { StatisticRow } from './components/StatisticRow';
import { format } from 'date-fns';
import useWidthMeasurement from '__hooks/useWidthMeasurement';
import { Button } from '@kizen/kds/Button';
import { useSelector } from 'react-redux';
import { ContentCard, PageWrapper } from '../styles';

const renderCustomObjectRow = (entry: any, barWidth: number, t: any) => {
  const { object, fieldsStats, recordStats } = entry;

  return (
    <div key={entry.key}>
      <div
        className="max-w-[var(--bar-width)] w-full"
        style={
          {
            '--bar-width': `${barWidth}px`,
          } as any
        }
      >
        <Typography size="lg" truncate>
          {object.name}
        </Typography>
      </div>
      <Spacer size={10} />
      <StatisticRow {...recordStats} barWidth={barWidth} label={t('Records')} />
      <Spacer size={15} />
      <StatisticRow {...fieldsStats} barWidth={barWidth} label={t('Fields')} />
      <Spacer size={30} />
    </div>
  );
};

type WarningMessageProps = {
  message: string;
  subjectLineAddition?: string;
};

const WarningMessage = (props: WarningMessageProps) => {
  const { message, subjectLineAddition } = props;
  const { t } = useTranslation();
  const business = useSelector((s: any) => s.authentication.chosenBusiness);

  return (
    <div className="bg-red-50 p-4 rounded-standard mt-4">
      <Typography color="red-900">{message}</Typography>
      <Spacer size={20} />
      <a
        href={`mailto:sales@kizen.com?subject=Upgrade Inquiry for "${business?.name ?? 'Business'}"${subjectLineAddition ? ` - ${subjectLineAddition}` : ''}`}
      >
        <Button maxWidth={false} color="primary">
          {t('Contact Sales')}
        </Button>
      </a>
    </div>
  );
};

export const UsagePage = () => {
  const { t } = useTranslation();
  const business = useSelector((s: any) => s.authentication.chosenBusiness);
  const [isShowMoreCustomObjects, setIsShowMoreCustomObjects] = useState(false);

  // get query param called `over`
  const over = new URLSearchParams(window.location.search).get('over');

  const simulateOverage = over === 'true' || over === '1';

  const { ref: cardRef, width: cardWidth } = useWidthMeasurement();

  const barWidth = cardWidth - 40;

  const { data: summaryData, isLoading: summaryUsageLoading } = useQuery(
    getSummaryUsageQuery()
  );
  const { data: businessLicenses, isLoading: businessLicensesLoading } =
    useQuery(getBusinessLicenseQuery());

  const generalPlan = useMemo(() => {
    return businessLicenses?.find(
      (l) => l.license_plan.plan_category === 'general'
    );
  }, [businessLicenses]);

  const automationMonthlyStats = useMemo(() => {
    if (!summaryData?.automations?.monthly_limit) {
      return {
        percentage: 0,
        used: summaryData?.automations?.monthly_usage ?? 0,
        limit: summaryData?.automations?.monthly_limit ?? Infinity,
        isOver: simulateOverage,
      };
    }

    const percentage =
      (summaryData.automations.monthly_usage /
        summaryData.automations.monthly_limit) *
      100;

    return {
      percentage,
      used: summaryData.automations.monthly_usage,
      limit: summaryData.automations.monthly_limit ?? Infinity,
      isOver: simulateOverage || percentage >= 100,
    };
  }, [summaryData, simulateOverage]);

  const automationDailyStats = useMemo(() => {
    if (!summaryData?.automations?.daily_limit) {
      return {
        percentage: 0,
        used: summaryData?.automations?.daily_usage ?? 0,
        limit: summaryData?.automations?.daily_limit ?? Infinity,
        isOver: simulateOverage,
      };
    }

    const percentage =
      (summaryData.automations.daily_usage /
        summaryData.automations.daily_limit) *
      100;

    return {
      percentage,
      used: summaryData.automations.daily_usage,
      limit: summaryData.automations.daily_limit ?? Infinity,
      isOver: simulateOverage || percentage >= 100,
    };
  }, [summaryData, simulateOverage]);

  const emailMonthlyStats = useMemo(() => {
    if (!summaryData?.emails?.monthly_limit) {
      return {
        percentage: 0,
        used: 0,
        limit: summaryData?.emails?.monthly_limit ?? Infinity,
        isOver: simulateOverage,
      };
    }

    const percentage = (0 / summaryData.emails.monthly_limit) * 100;

    return {
      percentage,
      used: 0,
      limit: summaryData.emails.monthly_limit ?? Infinity,
      isOver: simulateOverage || percentage >= 100,
    };
  }, [summaryData, simulateOverage]);

  const customObjectStats = useMemo(() => {
    const objectKeys = Object.keys(summaryData?.custom_objects ?? {});
    const objects = objectKeys.map((key) => {
      const matchingObject = (summaryData?.custom_objects as any)[key];

      const { stats, object } = matchingObject;

      const fieldPercentage = stats.fields_limit
        ? (stats.fields_usage / stats.fields_limit) * 100
        : 0;

      const fieldsStats = {
        percentage: fieldPercentage,
        used: stats.fields_usage ?? 0,
        limit: stats.fields_limit ?? Infinity,
        isOver: simulateOverage || fieldPercentage >= 100,
      };

      const recordPercentage = stats.records_limit
        ? (stats.records_usage / stats.records_limit) * 100
        : 0;

      const recordStats = {
        percentage: recordPercentage,
        used: stats.records_usage ?? 0,
        limit: stats.records_limit ?? Infinity,
        isOver: simulateOverage || recordPercentage >= 100,
      };

      return {
        key,
        object,
        stats,
        fieldsStats,
        recordStats,
      };
    });

    return objects.sort((a, b) => {
      const aMaxPercentage = Math.max(
        a.fieldsStats.percentage,
        a.recordStats.percentage
      );
      const bMaxPercentage = Math.max(
        b.fieldsStats.percentage,
        b.recordStats.percentage
      );

      return bMaxPercentage - aMaxPercentage;
    });
  }, [summaryData, simulateOverage]);

  const customObjectStatsTop = useMemo(() => {
    return customObjectStats.slice(0, 2);
  }, [customObjectStats]);

  const customObjectStatsBottom = useMemo(() => {
    return customObjectStats.slice(2);
  }, [customObjectStats]);

  const numberOfLimitedCustomObjectsByFields = useMemo(() => {
    return customObjectStats.filter((o) => o.fieldsStats.isOver).length;
  }, [customObjectStats]);

  const numberOfLimitedCustomObjectsByRecords = useMemo(() => {
    return customObjectStats.filter((o) => o.recordStats.isOver).length;
  }, [customObjectStats]);

  const numberOfLimitedCustomObjects =
    numberOfLimitedCustomObjectsByFields +
    numberOfLimitedCustomObjectsByRecords;

  if (summaryUsageLoading || businessLicensesLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      <div className="pb-20">
        <PageWrapper>
          <div className="grid grid-cols-2 gap-20">
            <ContentCard ref={cardRef}>
              <Typography variant="header" size="2xl" weight="semibold">
                {t('Usage')}
              </Typography>
              <Spacer size={20} />
              {generalPlan ? (
                <Typography>
                  {t(
                    'Monthly metrics reset at the end of your billing cycle, on'
                  )}{' '}
                  {format(new Date(generalPlan.renewal_date), 'MMMM dd, yyyy')}
                </Typography>
              ) : null}
              <Spacer size={30} />
              <Spacer size={10} />
              <div>
                <Typography size="xl" weight="semibold">
                  {t('Automations')}
                </Typography>
                {automationMonthlyStats.isOver ||
                automationDailyStats.isOver ? (
                  <WarningMessage
                    message={t(
                      'No automation credits remain for the {{period}}. Please upgrade your plan to continue using automations.',
                      {
                        period: automationMonthlyStats.isOver
                          ? t('billing cycle')
                          : t('day'),
                      }
                    )}
                    subjectLineAddition={t('Automations')}
                  />
                ) : null}
                <Spacer size={20} />
                <StatisticRow
                  {...automationMonthlyStats}
                  barWidth={barWidth}
                  label={t('Monthly')}
                />
                <Spacer size={15} />
                <StatisticRow
                  {...automationDailyStats}
                  barWidth={barWidth}
                  label={t('Daily')}
                  info={t('Usage is tracked in the UTC timezone')}
                />
                <Spacer size={30} />
                <Spacer size={10} />{' '}
                <Typography size="xl" weight="semibold">
                  {t('Marketing Platform')}
                </Typography>
                {emailMonthlyStats.isOver ? (
                  <WarningMessage
                    message={t(
                      'No email sends remain for the billing cycle. Please upgrade your plan to continue sending.'
                    )}
                    subjectLineAddition={t('Emails')}
                  />
                ) : null}
                <Spacer size={20} />
                <StatisticRow
                  {...emailMonthlyStats}
                  barWidth={barWidth}
                  label={t('Emails - Monthly')}
                />
                <Spacer size={30} />
                <Spacer size={10} />
                <Typography size="xl" weight="semibold">
                  {t('Custom Objects')}
                </Typography>
                {numberOfLimitedCustomObjects > 0 ? (
                  <WarningMessage
                    message={[
                      numberOfLimitedCustomObjectsByFields > 0
                        ? t(
                            '{{fieldCount}} custom objects have reached their field count limit.',
                            {
                              fieldCount: numberOfLimitedCustomObjectsByFields,
                            }
                          )
                        : '',
                      numberOfLimitedCustomObjectsByRecords > 0
                        ? t(
                            '{{recordCount}} custom objects have reached their record count limit.',
                            {
                              recordCount:
                                numberOfLimitedCustomObjectsByRecords,
                            }
                          )
                        : '',
                      t(
                        'Please upgrade your plan to continue adding {{toAdd}} to these objects.',
                        {
                          toAdd:
                            numberOfLimitedCustomObjectsByRecords > 0 &&
                            numberOfLimitedCustomObjectsByFields > 0
                              ? t('records and fields')
                              : numberOfLimitedCustomObjectsByRecords > 0
                                ? t('records')
                                : t('fields'),
                        }
                      ),
                    ].join(' ')}
                    subjectLineAddition={t('Custom Objects')}
                  />
                ) : null}
                <Spacer size={20} />
                <div>
                  {customObjectStatsTop.map((entry) =>
                    renderCustomObjectRow(entry, barWidth, t)
                  )}
                </div>
                {!isShowMoreCustomObjects && customObjectStats.length > 2 ? (
                  <div className="flex justify-center">
                    <Button
                      variant="text"
                      rightIcon="custom-chevron-down"
                      color="tertiary"
                      onClick={() => {
                        setIsShowMoreCustomObjects((prev) => !prev);
                      }}
                    >
                      {isShowMoreCustomObjects
                        ? t('Show less')
                        : t('Show more')}
                    </Button>
                  </div>
                ) : null}
                <div>
                  {isShowMoreCustomObjects
                    ? customObjectStatsBottom.map((entry) =>
                        renderCustomObjectRow(entry, barWidth, t)
                      )
                    : null}
                </div>
              </div>
            </ContentCard>
            <ContentCard>
              <div className="flex flex-col items-center gap-y-30 pt-10">
                <Typography variant="header" size="3xl" className="text-center">
                  {t('Contact Sales to Upgrade')}
                </Typography>
                <Typography className="text-center">
                  {t('Please contact sales to upgrade your business.')}
                </Typography>
                <a
                  href={`mailto:sales@kizen.com?subject=Upgrade Inquiry for "${business?.name ?? 'Business'}"`}
                >
                  <Button>{t('Contact Sales')}</Button>
                </a>
              </div>
            </ContentCard>
          </div>
        </PageWrapper>
      </div>
    </PageSizing>
  );
};
